import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "../lib/supabase";

export interface Review {
  id: string;
  venueId: string;
  userId: string;
  username: string;
  avatarUrl: string | null;
  displayName: string | null;
  rating: number;
  comment: string;
  date: string;
  helpful: number;
  verified: boolean;
}

export interface AddReviewResult {
  success: boolean;
  error?: string;
}

function mapRow(row: Record<string, unknown>): Review {
  return {
    id: row.id as string,
    venueId: row.venue_id as string,
    userId: row.user_id as string,
    username: row.username as string,
    avatarUrl: null,
    displayName: null,
    rating: row.rating as number,
    comment: row.comment as string,
    date: (row.created_at as string).split("T")[0],
    helpful: row.helpful as number,
    verified: row.verified as boolean,
  };
}

export function useReviews(venueId: string) {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [myVotes, setMyVotes] = useState<Set<string>>(new Set());
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  // Enrich reviews with profile data
  const enrichWithProfiles = useCallback(async (rawReviews: Review[]): Promise<Review[]> => {
    if (rawReviews.length === 0) return rawReviews;
    const userIds = [...new Set(rawReviews.map((r) => r.userId))];
    const { data: profiles } = await supabase
      .from("profiles")
      .select("id, username, display_name, avatar_url")
      .in("id", userIds);

    const profileMap: Record<string, { avatar_url: string | null; display_name: string | null; username: string }> = {};
    (profiles ?? []).forEach((p) => {
      profileMap[p.id as string] = {
        avatar_url: p.avatar_url as string | null,
        display_name: p.display_name as string | null,
        username: p.username as string,
      };
    });

    return rawReviews.map((r) => ({
      ...r,
      avatarUrl: profileMap[r.userId]?.avatar_url ?? null,
      displayName: profileMap[r.userId]?.display_name ?? null,
    }));
  }, []);

  const fetchReviews = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("reviews")
      .select("*")
      .eq("venue_id", venueId)
      .order("created_at", { ascending: false });

    if (!error && data) {
      const mapped = data.map(mapRow);
      const enriched = await enrichWithProfiles(mapped);
      setReviews(enriched);
    }
    setLoading(false);
  }, [venueId, enrichWithProfiles]);

  // Load user's existing helpful votes
  const fetchMyVotes = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;
    const { data } = await supabase
      .from("review_helpful_votes")
      .select("review_id")
      .eq("user_id", session.user.id);
    if (data) {
      setMyVotes(new Set(data.map((v) => v.review_id as string)));
    }
  }, []);

  useEffect(() => {
    fetchReviews();
    fetchMyVotes();

    const channel = supabase
      .channel(`reviews:venue_id=eq.${venueId}`)
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "reviews", filter: `venue_id=eq.${venueId}` },
        async (payload) => {
          const newReview = mapRow(payload.new as Record<string, unknown>);
          const [enriched] = await enrichWithProfiles([newReview]);
          setReviews((prev) => {
            if (prev.some((r) => r.id === enriched.id)) return prev;
            return [enriched, ...prev];
          });
        })
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "reviews", filter: `venue_id=eq.${venueId}` },
        (payload) => {
          const updated = mapRow(payload.new as Record<string, unknown>);
          setReviews((prev) =>
            prev.map((r) => r.id === updated.id ? { ...r, helpful: updated.helpful } : r)
          );
        })
      .on("postgres_changes", { event: "DELETE", schema: "public", table: "reviews", filter: `venue_id=eq.${venueId}` },
        (payload) => {
          const deletedId = (payload.old as Record<string, unknown>).id as string;
          setReviews((prev) => prev.filter((r) => r.id !== deletedId));
        })
      .subscribe();

    channelRef.current = channel;
    return () => {
      if (channelRef.current) { supabase.removeChannel(channelRef.current); channelRef.current = null; }
    };
  }, [venueId, fetchReviews, fetchMyVotes, enrichWithProfiles]);

  const averageRating = reviews.length > 0
    ? reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length
    : 0;

  const hasUserReviewed = useCallback(
    (userId: string) => reviews.some((r) => r.userId === userId),
    [reviews],
  );

  const addReview = useCallback(async (
    userId: string,
    username: string,
    rating: number,
    comment: string,
  ): Promise<AddReviewResult> => {
    const trimmed = comment.trim();
    if (trimmed.length < 10) return { success: false, error: "Yorum en az 10 karakter olmalıdır." };
    if (trimmed.length > 500) return { success: false, error: "Yorum 500 karakteri geçemez." };
    if (/https?:\/\//i.test(trimmed)) return { success: false, error: "Yorum içinde link paylaşılamaz." };
    if (/(.)\1{6,}/.test(trimmed)) return { success: false, error: "Geçersiz yorum içeriği." };

    const { error } = await supabase.from("reviews").insert({
      venue_id: venueId,
      user_id: userId,
      username,
      rating,
      comment: trimmed,
      helpful: 0,
      verified: false,
    });

    if (error) {
      if (error.code === "23505") return { success: false, error: "Bu mekan için zaten yorum yaptınız." };
      return { success: false, error: "Yorum eklenirken bir hata oluştu." };
    }

    await fetchReviews();
    return { success: true };
  }, [venueId, fetchReviews]);

  const toggleHelpful = useCallback(async (reviewId: string) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;
    const userId = session.user.id;
    const review = reviews.find((r) => r.id === reviewId);
    if (!review) return;

    const alreadyVoted = myVotes.has(reviewId);

    if (alreadyVoted) {
      // Remove vote
      await supabase.from("review_helpful_votes").delete()
        .eq("review_id", reviewId).eq("user_id", userId);
      await supabase.from("reviews").update({ helpful: Math.max(0, review.helpful - 1) }).eq("id", reviewId);
      setMyVotes((prev) => { const next = new Set(prev); next.delete(reviewId); return next; });
      setReviews((prev) => prev.map((r) => r.id === reviewId ? { ...r, helpful: Math.max(0, r.helpful - 1) } : r));
    } else {
      // Add vote
      const { error } = await supabase.from("review_helpful_votes").insert({ review_id: reviewId, user_id: userId });
      if (!error) {
        await supabase.from("reviews").update({ helpful: review.helpful + 1 }).eq("id", reviewId);
        setMyVotes((prev) => new Set(prev).add(reviewId));
        setReviews((prev) => prev.map((r) => r.id === reviewId ? { ...r, helpful: r.helpful + 1 } : r));
      }
    }
  }, [reviews, myVotes]);

  // Legacy alias
  const markHelpful = toggleHelpful;

  return { reviews, averageRating, addReview, hasUserReviewed, markHelpful, toggleHelpful, myVotes, loading, refetch: fetchReviews };
}
